import { css } from 'styled-components';
import { transition } from '../themes/mainTheme';

export type BoxedFieldProps = {
	$disabled?: boolean | undefined;
	$hasError?: boolean | undefined;
	/**
	 * Forces the focus look on a box that cannot hold focus itself — an open
	 * dropdown whose caret has moved into a portalled menu, so neither `:focus`
	 * nor `:focus-within` reaches it.
	 */
	$engaged?: boolean | undefined;
};

// The focus look on its own. A boxed field wears it on `:focus`, an open
// dropdown wears it while its caret has moved into a portalled menu, and a
// selected CardOption wears it to say "this is the current choice" — one
// definition so those three cannot drift apart.
export const fieldFocusRing = css`
	border-color: ${props => props.theme.form.input.focus.border};
	box-shadow:
		${props => props.theme.shadow.engaged},
		${props => props.theme.form.input.focus.shadow};
`;

// One definition of how a boxed form control answers the pointer and the caret.
// Input, SearchInput and the multi-select trigger sit next to each other in the
// same form, so any divergence between them reads as a bug rather than a style.
// The caller still owns the box — size, padding, fill, content — this owns only
// the stroke and the states it moves through.
export const boxedFieldStates = css<BoxedFieldProps>`
	border: ${props => props.theme.form.border.width}px
		solid ${props => props.theme.form.input.border};
	border-radius: ${props => props.theme.form.border.radius}px;
	transition: ${transition('border-color', 'box-shadow')};

	&:hover {
		border-color: ${props => props.theme.border.active};
	}

	// Pressed keeps the resting colour's brightness but thickens the stroke, so
	// the box reads as held without flashing a second colour on the way to focus.
	&:active {
		border-color: ${props => props.theme.border.active};
		box-shadow: ${props => props.theme.shadow.engaged};
	}

	// Typing and selected are the same box — the caret is what tells them apart.
	&:focus,
	&:focus-within {
		${fieldFocusRing};
		outline: 0;
	}

	${({ $engaged }) => $engaged && css`
		&, &:hover, &:active {
			${fieldFocusRing};
		}
	`}

	// Error and disabled outrank every state above: they say the same thing
	// wherever the pointer is.
	${({ $hasError, theme }) => $hasError && css`
		&, &:hover, &:active, &:focus, &:focus-within {
			border-color: ${theme.form.input.error.border};
			box-shadow: ${theme.form.input.error.shadow};
		}
	`}

	${({ $disabled, theme }) => $disabled && css`
		&, &:hover, &:active, &:focus, &:focus-within {
			pointer-events: none;
			background: ${theme.form.disabled.background};
			border-color: ${theme.form.input.border};
			box-shadow: none;
			opacity: ${theme.form.disabled.opacity};
		}
	`}
`;
